import { getThemeColor, LogManager } from "@mcleod/core";
import { CapacitorAccess } from "../capacitor";
import { MobileDeviceSystemBars, MobileSystemBar } from "./MobileDeviceSystemBars";

const log = LogManager.getLogger("common.SystemBarsAccessor");

export type SystemBarsStyle = "DARK" | "LIGHT" | "DEFAULT";

/** Red, green, blue 0-255 and alpha 0-1. */
export type RgbaColor = [number, number, number, number];
export type RgbColor = [number, number, number];

/**
 * Keeps the OS status bar (clock, battery, signal) and, on Android, the navigation bar (3-button back/home/recents)
 * legible against whatever the page is drawing under them.
 *
 * Capacitor's SystemBars naming follows the *bar* background: `DARK` means "the bar sits on a dark background,
 * draw light icons", `LIGHT` means the opposite.  With edge-to-edge rendering the web content is what sits behind
 * both bars, so the page has to tell the OS which it is; nothing does this automatically.  The two bars are styled
 * independently because they rarely sit on the same surface: a dark page header at the top and a light footer or a
 * white slideout at the bottom is the normal case.
 */
export class SystemBarsAccessor {
    private static get plugin(): MobileDeviceSystemBars | undefined {
        return CapacitorAccess.plugins?.SystemBars;
    }

    public static get isAvailable(): boolean {
        return this.plugin != null;
    }

    /**
     * Applies a style to one bar, or to every bar when `bar` is omitted.
     */
    public static async setStyle(style: SystemBarsStyle, bar?: MobileSystemBar): Promise<void> {
        try {
            await this.plugin?.setStyle(bar != null ? { style, bar } : { style });
        } catch (error) {
            log.debug("Unable to set system bar style", error);
        }
    }

    /**
     * Picks light or dark status bar icons for the given background and applies it to every bar.  Accepts anything the
     * theme understands (`"primary.darker"`, `"defaultBackground"`) as well as literal `#rgb`, `#rrggbb`,
     * `#rrggbbaa` and `rgb()` / `rgba()` values.
     */
    public static async syncToBackground(background: string | null | undefined, bar?: MobileSystemBar): Promise<void> {
        const style = SystemBarsAccessor.styleForBackground(background);
        if (style != null)
            await this.setStyle(style, bar);
    }

    /**
     * Reads the color actually painted behind the bars from the DOM, which is more trustworthy than a theme key when
     * company settings or a layout have overridden the header color.
     */
    public static async syncToElement(element: HTMLElement | null | undefined, bar?: MobileSystemBar): Promise<void> {
        if (element == null)
            return;
        await this.syncToBackground(window.getComputedStyle(element).backgroundColor, bar);
    }

    /**
     * Styles the status bar for whatever is painted at the top edge of the screen and the navigation bar for
     * whatever is painted at the bottom edge, overlays included.  Call whenever a route, overlay or footer changes.
     */
    public static async syncToScreenEdges(): Promise<void> {
        if (!this.isAvailable || typeof document.elementFromPoint !== "function")
            return;
        const x = Math.max(0, Math.floor(window.innerWidth / 2));
        const top = SystemBarsAccessor.effectiveBackgroundAt(x, 1);
        const bottom = SystemBarsAccessor.effectiveBackgroundAt(x, Math.max(0, window.innerHeight - 2));
        const topStyle = SystemBarsAccessor.styleForRgb(top);
        const bottomStyle = SystemBarsAccessor.styleForRgb(bottom);
        log.debug(`System bars: top ${top} -> ${topStyle}, bottom ${bottom} -> ${bottomStyle}`);
        if (topStyle != null)
            await this.setStyle(topStyle, "StatusBar");
        if (bottomStyle != null)
            await this.setStyle(bottomStyle, "NavigationBar");
    }

    /**
     * Pure helper (no plugin access) so it can be unit tested: returns the bar style that keeps icons visible
     * over `background`, or null when the color cannot be parsed or is fully transparent.
     */
    public static styleForBackground(background: string | null | undefined): SystemBarsStyle | null {
        const rgba = SystemBarsAccessor.parseColor(background);
        if (rgba == null || rgba[3] <= 0)
            return null;
        return SystemBarsAccessor.styleForRgb([rgba[0], rgba[1], rgba[2]]);
    }

    /**
     * Luminance above which dark icons beat light ones.  The pure WCAG crossover is 0.179, but brand blues such as the
     * McLeod primary (#137cdd, L ≈ 0.19) conventionally carry white chrome, so the split is nudged up: mid-greys such
     * as a dimmed overlay backdrop over white (L ≈ 0.5) still get dark icons, saturated header colors keep light ones.
     */
    private static readonly DARK_ICON_LUMINANCE_THRESHOLD = 0.35;

    public static styleForRgb(rgb: RgbColor | null): SystemBarsStyle | null {
        if (rgb == null)
            return null;
        return SystemBarsAccessor.relativeLuminance(rgb) < SystemBarsAccessor.DARK_ICON_LUMINANCE_THRESHOLD ? "DARK" : "LIGHT";
    }

    /** WCAG relative luminance of an sRGB color, 0 (black) to 1 (white). */
    public static relativeLuminance([r, g, b]: RgbColor): number {
        const channel = (c: number) => {
            const s = c / 255;
            return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
        };
        return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
    }

    /**
     * The color a user sees at (x, y): the element there and its ancestors are composited bottom-up, so a translucent
     * overlay backdrop over a white page reads as light grey rather than "transparent".
     */
    public static effectiveBackgroundAt(x: number, y: number): RgbColor | null {
        const element = document.elementFromPoint(x, y) as HTMLElement | null;
        return SystemBarsAccessor.effectiveBackgroundOf(element);
    }

    /**
     * Composites the background colors of `element` and its ancestors (nearest first) until an opaque one is found.
     * Returns null when nothing along the chain paints anything.
     */
    public static effectiveBackgroundOf(element: Element | null | undefined): RgbColor | null {
        const layers: RgbaColor[] = [];
        for (let current: Element | null = element ?? null; current != null; current = current.parentElement) {
            const parsed = SystemBarsAccessor.parseColor(window.getComputedStyle(current).backgroundColor);
            if (parsed != null && parsed[3] > 0) {
                layers.push(parsed);
                if (parsed[3] >= 1)
                    break;
            }
        }
        return SystemBarsAccessor.compositeLayers(layers);
    }

    /**
     * Alpha-composites layers given nearest (topmost) first.  A chain that never reaches an opaque color is
     * composited over white, matching what the WebView paints behind an unstyled document.
     */
    public static compositeLayers(layersTopFirst: RgbaColor[]): RgbColor | null {
        if (layersTopFirst.length === 0)
            return null;
        let result: RgbColor = [255, 255, 255];
        for (let i = layersTopFirst.length - 1; i >= 0; i--) {
            const [r, g, b, a] = layersTopFirst[i];
            const alpha = Math.min(1, Math.max(0, a));
            result = [
                Math.round(r * alpha + result[0] * (1 - alpha)),
                Math.round(g * alpha + result[1] * (1 - alpha)),
                Math.round(b * alpha + result[2] * (1 - alpha))
            ];
        }
        return result;
    }

    /**
     * Parses `#rgb`, `#rgba`, `#rrggbb`, `#rrggbbaa`, `rgb()`, `rgba()`, `transparent` and theme keys.  Returns null
     * for anything it cannot read.
     */
    public static parseColor(value: string | null | undefined): RgbaColor | null {
        if (value == null || value.trim().length === 0)
            return null;
        let color = value.trim();
        if (color.toLowerCase() === "transparent")
            return [0, 0, 0, 0];
        if (!color.startsWith("#") && !color.startsWith("rgb"))
            color = getThemeColor(color) ?? color;
        if (color == null)
            return null;
        if (color.startsWith("#")) {
            let hex = color.substring(1);
            if (hex.length === 3 || hex.length === 4)
                hex = hex.split("").map(ch => ch + ch).join("");
            if (hex.length !== 6 && hex.length !== 8)
                return null;
            const n = parseInt(hex.substring(0, 6), 16);
            if (Number.isNaN(n))
                return null;
            const alpha = hex.length === 8 ? parseInt(hex.substring(6, 8), 16) / 255 : 1;
            return [(n >> 16) & 255, (n >> 8) & 255, n & 255, alpha];
        }
        const match = color.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i);
        if (match == null)
            return null;
        return [parseFloat(match[1]), parseFloat(match[2]), parseFloat(match[3]), match[4] != null ? parseFloat(match[4]) : 1];
    }
}
